/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("WidgetBase", ["EventListener"], function (context, cls) {
  const SPACES_RE = /\s+/;

  /**
   * Base class for widgets.
   * @class WidgetBase
   * @memberOf classes
   * @tutorial widgets
   * @extends classes.EventListener
   * @publicdoc Widgets
   */
  cls.WidgetBase = context.oo.Class(
    {
      base: cls.EventListener
    },
    function ($super) {
      const __charMeasurer = document.createElement("char-measurer");
      __charMeasurer.className = "g_layout_charMeasurer";
      const __charMeasurer1 = document.createElement("char-measurer-item");
      __charMeasurer1.className = "g_layout_charMeasurer1";
      __charMeasurer1.textContent = "MMMMMMMMMM\nM\nM\nM\nM\nM\nM\nM\nM\nM";
      const __charMeasurer2 = document.createElement("char-measurer-item");
      __charMeasurer2.className = "g_layout_charMeasurer2";
      __charMeasurer2.textContent = "0000000000";
      __charMeasurer.appendChild(__charMeasurer1);
      __charMeasurer.appendChild(__charMeasurer2);
      __charMeasurer.setAttribute("aria-hidden", "true");

      return /** @lends classes.WidgetBase.prototype */ {
        $static: /** @lends classes.WidgetBase */ {
          /** Generic click events handler */
          // TODO is it still necessary to have these methods static ?
          /** Generic focus events handler */
          _onFocus: function (event) {
            this.emit(context.constants.widgetEvents.focus, event);
          },
          /**
           * Need to listen mouseup event on body to be able to focus an input field if selection ends outside the field.
           * If selection ends inside the field, click event will be raised
           * @protected
           */
          _onSelect: function () {
            cls.DOMElementHelper.on(
              document.body,
              "mouseup.DetectTextSelection",
              function (event) {
                cls.DOMElementHelper.off(document.body, "mouseup.DetectTextSelection");
                cls.DOMElementHelper.off(this._element, "mouseleave.DetectTextSelection");
              }.bind(this)
            );
            cls.DOMElementHelper.on(
              this._element,
              "mouseleave.DetectTextSelection",
              function (event) {
                cls.DOMElementHelper.off(document.body, "mouseup.DetectTextSelection");
                cls.DOMElementHelper.off(this._element, "mouseleave.DetectTextSelection");
                if (event.buttons === 1) {
                  if ((this.isInTable() && !event.ctrlKey) || !this.isInTable()) {
                    this._onRequestFocus(event); // request focus
                  }
                }
              }.bind(this)
            );
          },
          selfDataContent: {}
        },
        __name: "WidgetBase",
        /** @type {string} */
        __templateName: null,
        __charMeasurer: null,
        __dataContentPlaceholderSelector: null,
        /**
         * Current widget's unique ID (GBC system wide)
         * @type {?string}
         */
        _uuid: null,

        /**
         * Incremental ID for widgets that are linked to the AUI, 0 otherwise
         * @type {number}
         */
        _nUuid: 0,

        /**
         * Widget root class name (based on widget's unique ID)
         * @type {?string}
         */
        _rootClassName: null,
        _auiTag: null,
        _auiName: null,
        /**
         * the dom element
         * @type {HTMLElement}
         * @protected
         */
        _element: null,
        /**
         * the parent widget
         * @type {classes.WidgetGroupBase}
         * @protected
         */
        _parentWidget: null,
        /**
         * Current instance stylesheet
         * @type {Object}
         */
        _stylesheet: null,
        /**
         * stylesheet context ('global', 'window')
         * @type {string}
         */
        _stylingContext: "global",
        /**
         * the layout engine
         * @type {classes.LayoutEngineBase}
         * @protected
         */
        _layoutEngine: null,
        /**
         * the layout information
         * @type {classes.LayoutInformation}
         * @protected
         */
        _layoutInformation: null,
        /**
         * the user interface widget
         * @type {classes.UserInterfaceWidget}
         * @protected
         */
        _uiWidget: null,
        /**
         * Application widget
         * @type {classes.ApplicationWidget}
         * @protected
         */
        _appWidget: null,
        _appHash: null,
        _windowWidget: null,
        _formWidget: null,
        _tableWidgetBase: null,
        _scrollGridWidget: null,
        _stretchableScrollGridWidgetBase: null,
        _folderWidget: null,
        _pageWidget: null,

        /**
         * list of translated text to set as content
         */
        _i18NDataList: null,
        /**
         * list of translated text to set as title
         */
        _i18NTitleList: null,
        _i18nTranslateListener: null,

        /**
         * Dialog type of the widget (Input, Input Array, Display, Display Array, Construct)
         * @type {?string}
         * @protected
         */
        _dialogType: null,
        _enabled: true,
        _noBorder: false,
        _hidden: false,
        _isVMFocusable: false,
        /** @type {boolean} */
        _isSystemWidget: false,

        /** @type {?Element} */
        _preSystemFocusedElement: null,

        _startKey: null,
        _endKey: null,

        _inMatrix: false,
        _inTable: false,
        _inScrollGrid: false,
        _inFirstTableRow: false,
        _inToolBar: false,
        _ignoreLayout: false,

        // arabic mode
        _isReversed: false,

        /**
         * @type {?string}
         */
        _rawStyles: null,

        /**
         * @type {Array<string>}
         */
        _applicationStyles: null,

        /**
         * An interruptible widget is active when the VM is processing
         * @type {boolean}
         */
        _interruptable: false,
        _hasWebcomp: false,

        /**
         * The real AUI widget
         * @type {classes.WidgetBase}
         */
        _realWidget: null,

        /**
         * True if the widget accept events when inside an inactive windows
         * @type {boolean}
         */
        _acceptEventWhenWindowInactive: null,

        /**
         * All the applied behaviors;
         * @type {Set}
         */
        _applyedBehaviors: null,

        /**
         * @inheritDoc
         * @constructs
         * @param {Object} opts instantiation options
         * @param {number} opts.appHash internal app hash
         * @param {classes.ApplicationWidget} opts.appWidget early ApplicationWidget link
         * @param {number} opts.auiTag internal aui tag id
         * @param {boolean} opts.inTable internal is in table
         * @param {boolean} opts.inMatrix internal is in matrix
         * @param {boolean} opts.inFirstTableRow internal
         * @param {boolean} opts.inScrollGrid internal is in a scroll grid
         * @param {boolean} opts.inToolBar internal is in a toolbar
         * @param {boolean} opts.ignoreLayout ignore layout char measurer
         */
        constructor: function (opts) {
          opts = opts || {};
          this._realWidget = opts.realWidget;
          this._appHash = opts.appHash;
          this._appWidget = opts.appWidget;
          this._auiTag = opts.auiTag;
          this._inTable = opts.inTable === true;
          this._inFirstTableRow = opts.inFirstTableRow === true;
          this._inMatrix = opts.inMatrix === true;
          this._inScrollGrid = opts.inScrollGrid === true;
          this._inToolBar = opts.inToolBar === true;
          this._ignoreLayout = (this._inTable && !this._inFirstTableRow) || opts.ignoreLayout;
          this._isSystemWidget = this._isSystemWidget || opts.isSystemWidget === true;

          this._applyedBehaviors = new Set();
          this._uuid = context.InitService.uniqueIdAsString();
          this._nUuid = this._auiTag ? context.InitService.uniqueId() : 0;
          this._acceptEventWhenWindowInactive = false;
          $super.constructor.call(this, opts);
          this.__templateName = this.__templateName ?? this.__name;
          this._rootClassName = "w_" + this._uuid;
          this._initElement();
          this._afterInitElement();
          this._initLayout();
          this._initTranslation();
          if (this._auiTag) {
            this.addClasses("aui__" + this._auiTag);
            this._element.dataset.auiId = this._auiTag;
          }
          context.WidgetService._emit(context.constants.widgetEvents.created, this);
          context.WidgetService.registerWidget(this);
        },

        /**
         * get the real AUI Widget
         * @return {classes.WidgetBase}
         */
        getAUIWidget: function () {
          return this._realWidget;
        },

        /**
         * Define the widget layout on traditional mode
         * @param {!number} letterSpacing - letter spacing in pixel
         * @param {!number} fieldHeight - field height in pixel
         * @param {!number} heightPadding - height padding between 2 lines
         */
        traditionalDisplay: function (letterSpacing, fieldHeight, heightPadding) {
          const layoutInfo = this.getLayoutInformation();

          if (layoutInfo) {
            const left = layoutInfo.getGridX();
            const top = layoutInfo.getGridY() * (fieldHeight + 2 * heightPadding) + heightPadding;
            const width = layoutInfo.getGridWidth();
            const height = layoutInfo.getGridHeight() * fieldHeight;

            cls.DOMElementHelper.toggleClass(layoutInfo.getHostElement(), layoutInfo.className, true);
            const style = this._element.parentElement.style;
            style.left = "calc(" + left + "ch + " + left + " * " + letterSpacing + ")";
            style.top = top + "px";
            style.width = "calc(" + width + "ch + " + width + " * " + letterSpacing + ")";
            style.height = height + "px";
          }
        },

        /**
         * Returns build parameters
         * @returns {{appHash: (null|*), auiTag: (null|*), inTable: (boolean|*), inFirstTableRow: (boolean|*), inMatrix: (boolean|*), inScrollGrid: *, ignoreLayout: (boolean|*)}} build parameters
         * @publicdoc
         */
        getBuildParameters: function () {
          return {
            realWidget: this._realWidget,
            appHash: this._appHash,
            appWidget: this._appWidget,
            auiTag: this._auiTag,
            inTable: this._inTable,
            inFirstTableRow: this._inFirstTableRow,
            inMatrix: this._inMatrix,
            inScrollGrid: this._inScrollGrid,
            inToolBar: this._inToolBar,
            ignoreLayout: this._ignoreLayout
          };
        },

        /**
         * Destroy style sheet related to widget
         * @private
         */
        _destroyStyle: function () {
          context.styler.removeStyleSheet(this.getUniqueIdentifier());
          if (this._stylingContext === "window") {
            const win = this.getWindowWidget();
            const sheetId = (win && win.getUniqueIdentifier()) || this._appHash || "_";
            context.styler.appendStyleSheet({}, this.getRootClassName(), true, sheetId);
          } else {
            context.styler.appendStyleSheet({}, this.getRootClassName(), true, this._appHash || "_");
          }
        },

        /**
         * @inheritDoc
         */
        destroy: function () {
          if (this._isSystemWidget) {
            this._deactivateSystemWidget();
          }

          this._destroyStyle();

          if (this._i18nTranslateChangeListener) {
            this._i18nTranslateChangeListener();
            this._i18nTranslateChangeListener = null;
          }
          this.emit(context.constants.widgetEvents.destroyed, this);
          context.WidgetService._emit(context.constants.widgetEvents.destroyed, this);
          if (this._layoutEngine) {
            this._layoutEngine.destroy();
            this._layoutEngine = null;
          }
          if (this._parentWidget && this._parentWidget.removeChildWidget) {
            this._parentWidget.removeChildWidget(this);
          }
          if (this._layoutInformation) {
            this._layoutInformation.destroy();
            this._layoutInformation = null;
          }
          cls.DOMElementHelper.off(document.body, "mouseup.DetectTextSelection");
          if (this._element) {
            this._element.remove();
          }

          if (this._applyedBehaviors) {
            this._applyedBehaviors.clear();
            this._applyedBehaviors = null;
          }

          this.__charMeasurer1 = null;
          this.__charMeasurer2 = null;
          this.__charMeasurer = null;
          this._stylesheet = null;

          this._uiWidget = null;
          this._appWidget = null;
          this._windowWidget = null;
          this._formWidget = null;
          this._tableWidgetBase = null;
          this._scrollGridWidget = null;
          this._stretchableScrollGridWidgetBase = null;
          this._folderWidget = null;
          this._element = null;

          if (this._i18nTranslateListener) {
            this._i18nTranslateListener();
            this._i18nTranslateListener = null;
          }
          this._i18NDataList = null;
          this._i18NTitleList = null;

          $super.destroy.call(this);

          context.WidgetService.unregisterWidget(this);
        },

        /**
         * The hook given in parameter will be triggered when the widget is destroyed
         * @param {Function(evt, emitter, ...args)} hook Function to call
         */
        onDestroy: function (hook) {
          return this.when(context.constants.widgetEvents.destroyed, hook);
        },

        /**
         * Method called after the element is initialized
         * Override in inherited widgets if necessary
         * As it's not up to the child widget to control the fact that it is in a table, scrollgrid or any other parent
         * @private
         */
        _afterInitElement: function () {
          this.updateCssClassesFromParenting();
        },

        /**
         * Create all instances for layout management
         * @protected
         */
        _initLayout: function () {
          this._layoutInformation = new cls.LayoutInformation(this);
          this._layoutEngine = new cls.NoLayoutEngine(this);
        },

        /**
         * function to be called by widget's layout engine when resetting the layout if needed
         */
        resetLayout: function () {},

        /**
         * Get the widget's layout information
         * @returns {classes.LayoutInformation} the widget's layout information
         * @publicdoc
         */
        getLayoutInformation: function () {
          return this._layoutInformation;
        },

        /**
         * Get the widget's layout engine
         * @returns {classes.LayoutEngineBase} the widget's layout engine
         * @publicdoc
         */
        getLayoutEngine: function () {
          return this._layoutEngine;
        },

        /**
         * Get the styling context of widget style sheet (global, window or widget);
         * @returns {string} widget styling context used in its style sheet
         */
        getStylingContext: function () {
          return this._stylingContext;
        },

        /**
         * Setups the DOM element
         * @protected
         */
        _initElement: function () {
          this._element = context.TemplateService.renderDOM(this.__templateName, this.__ascendance);
          const id = this.getRootClassName();
          this._element.id = id;

          this._element.className += ["", this.__ascendanceClasses, id, "g_measureable"].join(" ");

          if (!this._ignoreLayout) {
            this._initCharMeasurer();
          }
          if (this._isSystemWidget) {
            this._initAsSystemWidget();
          }
        },

        /**
         * Initialize this widget as a system widget.
         * Called automatically from _initElement() when _isSystemWidget is true.
         * Override in subclasses to add widget-specific system widget setup (call $super).
         * Note: focus tracking is started/stopped by KeyboardService.registerSystemWidget/unregisterSystemWidget.
         */
        _initAsSystemWidget: function () {
          this.addClasses("gbc_SystemWidget");
        },

        /**
         * Init the char Measurer for proper layout management
         * @private
         */
        _initCharMeasurer: function () {
          this.__charMeasurer = __charMeasurer.cloneNode(true);
          this.__charMeasurer1 = this.__charMeasurer.children[0];
          this.__charMeasurer2 = this.__charMeasurer.children[1];
          this._element.appendChild(this.__charMeasurer);
        },

        /**
         * Handle request Focus
         * @param {UIEvent} event - dom event
         */
        _onRequestFocus: function (event) {
          const isFocusable = this.isVMFocusable() && (this.isEnabled() || this.getDialogType() === "DisplayArray");
          if (this.isInTable()) {
            this.getTableWidgetBase().requestFocusFromWidget(this, event);
            // TODO check if test isInMatrix is still necessary with bellow check Display Array
          } else if (this.isInMatrix() || this.isInScrollGrid() || isFocusable) {
            this.emit(context.constants.widgetEvents.requestFocus, event);
          }
        },

        /**
         * Returns id widget should show application contextmenu
         * @returns {boolean} true if application contextmenu should be displayed
         */
        shouldShowApplicationContextMenu: function () {
          return true;
        },

        /**
         * Build/add extra actions to app contextmenu
         * Must be redefined by widget which must add extra actions
         * @param {classes.ContextMenuWidget} contextMenu - context menu that will host the actions
         * @param {classes.WidgetBase?} contextMenu - The widget that invoked the context menu
         */
        buildExtraContextMenuActions: function (contextMenu, sourceWidget = this) {
          // Clipboard actions are only added if clipboard API is available
          if (!context.ClipboardService.isApiAvailable()) {
            if (this.getParentWidget()) {
              this.getParentWidget().buildExtraContextMenuActions(contextMenu, sourceWidget);
            }
            return;
          }

          let copyFunction = null;
          let valueForClipboard = this.getValueForClipboard();
          const authorizedActions = this.getContextMenuAuthorizedActions();

          if (authorizedActions.copy && valueForClipboard) {
            copyFunction = function (contextMenu) {
              contextMenu.hide();
              gbc.ClipboardService.copyFromWidget(this, valueForClipboard).then(null);
            }.bind(this);
          }

          if (authorizedActions.cut && valueForClipboard) {
            const cutFunction = function (contextMenu) {
              contextMenu.hide();
              gbc.ClipboardService.cutFromWidget(this);
            };
            contextMenu.addAction(
              "cut",
              gbc.I18NService.t("gwc.clipboard.cut"),
              null,
              null,
              {
                clickCallback: cutFunction.bind(this, contextMenu)
              },
              true
            );
          }

          if (authorizedActions.paste) {
            const pasteFunction = function (contextMenu) {
              contextMenu.hide();
              context.ClipboardService.pasteToWidget(this).then(null);
            };

            const pasteWidget = contextMenu.addAction(
              "paste",
              gbc.I18NService.t("gwc.clipboard.paste"),
              null,
              null,
              {
                clickCallback: pasteFunction.bind(this, contextMenu)
              },
              true
            );

            context.ClipboardService.getClipboardData(false).then(function (txt) {
              // disabled paste action if there is nothing in the clipboard
              pasteWidget?.setEnabled(Boolean(txt));
            });
          }

          if (copyFunction && (!this.isInArray() || this.getValueForClipboard(true) !== valueForClipboard)) {
            contextMenu.addAction(
              "copy",
              gbc.I18NService.t("gwc.clipboard.copy"),
              null,
              "Control+C",
              {
                clickCallback: copyFunction.bind(this, contextMenu)
              },
              true
            );
          }

          if (this.getParentWidget()) {
            this.getParentWidget().buildExtraContextMenuActions(contextMenu, sourceWidget);
          }
        },

        /**
         * Declare whether the widget is a legitimate VM focus target.
         * When true, a click on the widget emits the `requestFocus` event
         * which lets the VM synchronize its focus chain. This is a pure
         * state setter — DOM `tabindex` is NOT manipulated here; static
         * `tabindex="0"` in the widget template (or a native focusable
         * descendant) is the source of Tab-order focus.
         * @param {boolean} focusable - State of VM focusable
         * @publicdoc
         */
        setVMFocusable: function (focusable) {
          this._isVMFocusable = focusable;
        },

        /**
         * Returns if the widget is declared as a VM focus target.
         * @return {boolean} State of VM focusable
         * @publicdoc
         */
        isVMFocusable: function () {
          return this._isVMFocusable;
        },

        /**
         * Tests if the widget has really the DOM focus (check document.activeElement)
         * @returns {boolean} true if the widget has the DOM focus
         */
        hasDOMFocus: function () {
          return this._element === document.activeElement;
        },

        /**
         * Tests if the widget contains the current DOM focus, descendants included (unlike
         * `hasDOMFocus`, which only matches the widget's own focusable element). Useful for
         * composite widgets whose inner element holds the focus (e.g. a DateEdit picker button).
         * @returns {boolean} true if the active element is the widget root or any descendant
         */
        containsDOMFocus: function () {
          return Boolean(this._element?.contains(document.activeElement));
        },

        /**
         * Accessibility mode only: true when DOM focus already lives inside this widget, so a
         * programmatic re-focus (scroll offset request, VM focus restore) would steal focus the
         * browser/user currently holds. Guards such re-asserts to avoid trapping TAB.
         * @returns {boolean}
         */
        browserOwnsDOMFocusInA11yMode: function () {
          return gbc.KeyboardService.isAccessibilityModeEnabled() && this.containsDOMFocus();
        },

        /**
         * Set the DOM focus to the widget
         * @param {boolean} [preventScroll] - if true prevent auto scroll
         * @publicdoc
         */
        domFocus: function (preventScroll = false) {
          if (!this._element) {
            return;
          }
          cls.DOMElementHelper.domFocus(this._element, preventScroll);
        },

        /**
         * Initialization of internationalization engine
         * @private
         */
        _initTranslation: function () {
          // Will ask the translation once ready
          this._i18NDataList = this._element.querySelectorAll("[data-i18n]");
          this._i18NTitleList = this._element.querySelectorAll("[title-i18n]");
          this._i18nTranslateListener = context.I18NService.translate(this);
          this._i18nTranslateChangeListener = context.I18NService.whenLangChange(
            function () {
              context.I18NService.translate(this);
            }.bind(this)
          );
        },

        /**
         * Translate the widget
         * @publicdoc
         */
        translate: function () {
          for (const element of this._i18NDataList) {
            element.innerHTML = gbc.I18NService.t(element.dataset.i18n ?? null);
          }
          for (const element of this._i18NTitleList) {
            element.setAttribute("title", gbc.I18NService.t(element.getAttribute("title-i18n")));
          }
        },

        /**
         * Get the unique identifier of the widget
         * @returns {string} the unique identifier of the widget
         * @publicdoc
         */
        getUniqueIdentifier: function () {
          return this._uuid;
        },

        /**
         * Get the increment identifier of the widget if linked to AUI, 0 otherwise
         * @returns {number} the increment identifier of the widget if linked to AUI, 0 otherwise
         */
        getAuiLinkedUniqueIdentifier: function () {
          return this._nUuid;
        },

        /**
         * Get the unique identifier of the application
         * @returns {string} the unique identifier of the application
         * @publicdoc
         */
        getApplicationIdentifier: function () {
          return this._appHash !== undefined ? this._appHash : null;
        },

        /**
         * Get the root element of the widget
         * @returns {HTMLElement} the root element of the widget
         * @publicdoc
         */
        getElement: function () {
          return this._element;
        },

        /**
         * Get the main class name of the widget
         * @return {string} the main class name
         * @publicdoc
         */
        getClassName: function () {
          return "gbc_" + this.__name;
        },

        /**
         * Build and return a BEM Syntaxed CSS class for the sub part of the widget
         * This method use the Template name, as Widget aprts are defined in the HTML Template
         * @param {string} part The WidgetPart name
         * @returns {string} .gbc_WidgetClass__part
         */
        getBEMPartName: function (part) {
          return `gbc_${this.__templateName}__${part}`;
        },

        /**
         * Get the name of the widget class
         * @return {string} the widget class name
         * @publicdoc
         */
        getName: function () {
          return this.__name;
        },

        /**
         * Get the Aui Tree Tag
         * @return {?number} aui tree tag
         */
        getAuiTag: function () {
          return this._auiTag;
        },

        /**
         * Get the Aui Tree Tag
         * @return {string} html class ready name
         * @private
         */
        _getAuiTagClass: function () {
          return ".aui__" + this._auiTag;
        },

        /**
         * Get the unique class name identifying a widget instance
         * @returns {*|string} the unique class name identifying a widget instance
         */
        getRootClassName: function () {
          return this._rootClassName;
        },

        /**
         * Get the CSS id selector of the widget
         * @param {string=} [subSelector] selector targeting an element below the widget's root node
         * @param {boolean=} [appliesOnRoot] true if the returned selector should match the root too.
         * @param {string} [preSelector] pre selector rule, if any
         * @returns {string} the CSS selector corresponding to the requested DOM element
         * @public
         */
        _getCssSelector: function (subSelector, appliesOnRoot, preSelector) {
          return (preSelector || "") + "#" + this.getRootClassName() + (appliesOnRoot ? "" : " ") + (subSelector || "");
        },

        /**
         * Get widget style property value
         * @param {?string} [selector] additional sub selector
         * @param {string} property property name
         * @param {boolean=} appliesOnRoot - true if the returned selector should match the root too.
         * @returns {*} property value if set, undefined otherwise
         * @publicdoc
         */
        getStyle: function (selector, property, appliesOnRoot) {
          if (!property) {
            property = selector;
            selector = null;
          }
          const cssSelector = this._getCssSelector(selector, appliesOnRoot);
          return this._stylesheet && this._stylesheet[cssSelector] && this._stylesheet[cssSelector][property];
        },

        /**
         * Updates widget style with new rules
         * @param {?string|{selector:String, preSelector:String, appliesOnRoot:boolean=}} [selector] additional sub selector
         * @param {Object.<string, *>} style style properties to set
         * @publicdoc
         */
        setStyle: function (selector, style) {
          if (!style) {
            style = selector;
            selector = null;
          }
          let subSelector = selector,
            preSelector = null,
            appliesOnRoot = null;
          if (selector && (selector.selector || selector.preSelector)) {
            subSelector = selector.selector;
            preSelector = selector.preSelector;
            appliesOnRoot = selector.appliesOnRoot;
          }
          const cssSelector = this._getCssSelector(subSelector, appliesOnRoot, preSelector);
          if (!this._stylesheet) {
            this._stylesheet = {};
          }
          let localStyle = this._stylesheet[cssSelector];
          if (!localStyle) {
            localStyle = this._stylesheet[cssSelector] = {};
          }
          const keys = Object.keys(style);
          for (const element of keys) {
            if (style[element] === null) {
              delete localStyle[element];
            } else {
              localStyle[element] = style[element];
            }
          }
          const win = this.getWindowWidget(),
            contextChanged = (this._stylingContext === "global" && win) || (this._stylingContext === "window" && !win);

          context.styler.appendStyleSheet(
            this._stylesheet,
            this.getRootClassName(),
            true,
            this._stylingContext === "widget" ? this.getUniqueIdentifier() : this.getStyleSheetId()
          );

          if (contextChanged) {
            this._stylingContext = win ? "window" : "global";
            if (win) {
              context.styler.appendStyleSheet({}, this.getRootClassName(), true, this._appHash || "_");
            }
          }
        },

        /**
         * Retrieve stylesheet id which stores all widget css rules
         * @returns {string|string}
         */
        getStyleSheetId: function () {
          const windowWidget = this.getWindowWidget(),
            windowWidgetId = windowWidget && windowWidget.getUniqueIdentifier();
          return windowWidgetId || this._appHash || "_";
        },

        /**
         * Get the raw styles from VM
         * @returns {?string} the raw styles from VM
         */
        getRawStyles: function () {
          return this._rawStyles;
        },
        setApplicationStyles: function (styles) {
          this._rawStyles = styles;
          let i;
          const oldClasses = this._applicationStyles,
            oldlen = oldClasses ? oldClasses.length : 0,
            newClasses = styles && styles.split(SPACES_RE),
            newlen = newClasses ? newClasses.length : 0;
          for (i = 0; i < oldlen; i++) {
            if (!newClasses || newClasses.indexOf(oldClasses[i]) < 0) {
              this.removeClass("gbc_style_" + oldClasses[i]);
            }
          }
          for (i = 0; i < newlen; i++) {
            if (!oldClasses || oldClasses.indexOf(newClasses[i]) < 0) {
              this.addClasses("gbc_style_" + newClasses[i]);
            }
          }
          this._applicationStyles = newClasses;
        },

        /**
         * Defines the parent widget
         * @param {classes.WidgetGroupBase} widget - the widget to use as parent
         * @param {Object=} options - possible options
         * @param {boolean=} options.noLayoutInvalidation - won't affect parent layout
         * @publicdoc
         */
        setParentWidget: function (widget, options) {
          options = options || {};
          this._parentWidget = widget;
          if (this._layoutEngine && !options.noLayoutInvalidation) {
            this._layoutEngine.invalidateMeasure();
          }
        },

        /**
         * Get the parent widget
         * @param {*?} type - class name to look for
         * @returns {classes.WidgetGroupBase} the parent widget
         * @publicdoc
         */
        getParentWidget: function (type) {
          let result = this._parentWidget;

          if (type) {
            while (result && !result.isInstanceOf(type)) {
              result = result.getParentWidget(type);
            }
          }
          return result;
        },

        /**
         * Get the UI widget related to the widget
         * @returns {classes.UserInterfaceWidget} UserInterfaceWidget
         * @publicdoc
         */
        getUserInterfaceWidget: function () {
          if (this._uiWidget === null) {
            if (this.isInstanceOf(gbc.classes.UserInterfaceWidget)) {
              this._uiWidget = this;
            } else {
              let result = this.getParentWidget();
              while (result && !result.isInstanceOf(gbc.classes.UserInterfaceWidget)) {
                result = result.getParentWidget();
              }
              this._uiWidget = result;
            }
          }
          return this._uiWidget;
        },

        /**
         * Get Application Widget related to the widget
         * @returns {classes.ApplicationWidget} ApplicationWidget
         * @publicdoc
         */
        getApplicationWidget: function () {
          if (this._appWidget === null) {
            if (this.isInstanceOf(gbc.classes.ApplicationWidget)) {
              this._appWidget = this;
            } else {
              let result = this.getParentWidget();
              while (result && !result.isInstanceOf(gbc.classes.ApplicationWidget)) {
                result = result.getParentWidget();
              }
              this._appWidget = result;
            }
          }
          return this._appWidget;
        },

        /**
         * Get the Window Widget related to the widget
         * @returns {classes.WindowWidget} WindowWidget
         * @publicdoc
         */
        getWindowWidget: function () {
          if (this._windowWidget === null) {
            if (this.isInstanceOf(gbc.classes.WindowWidget)) {
              this._windowWidget = this;
            } else {
              let result = this.getParentWidget();
              while (result && !result.isInstanceOf(gbc.classes.WindowWidget)) {
                result = result.getParentWidget();
              }
              this._windowWidget = result;
            }
          }
          return this._windowWidget;
        },

        /**
         * Get the Form Widget related to the widget
         * @returns {classes.FormWidget} FormWidget
         * @publicdoc
         */
        getFormWidget: function () {
          if (this._formWidget === null) {
            if (this.isInstanceOf(gbc.classes.FormWidget)) {
              this._formWidget = this;
            } else {
              let result = this.getParentWidget();
              while (result && !result.isInstanceOf(gbc.classes.FormWidget)) {
                result = result.getParentWidget();
              }
              this._formWidget = result;
            }
          }
          return this._formWidget;
        },

        /**
         * Get the table Widget base class related to the widget
         * @returns {classes.TableWidgetBase} TableWidgetBase
         * @publicdoc
         */
        getTableWidgetBase: function () {
          if (this._tableWidgetBase === null) {
            if (this.isInstanceOf(gbc.classes.TableWidgetBase)) {
              this._tableWidgetBase = this;
            } else {
              let result = this.getParentWidget();
              while (result && !result.isInstanceOf(gbc.classes.TableWidgetBase)) {
                result = result.getParentWidget();
              }
              this._tableWidgetBase = result;
            }
          }
          return this._tableWidgetBase;
        },

        /**
         * Get the scrollgrid Widget class related to the widget
         * @returns {classes.ScrollGridWidget} ScrollGridWidget
         * @publicdoc
         */
        getScrollGridWidget: function () {
          if (this._scrollGridWidget === null) {
            if (this.isInstanceOf(gbc.classes.ScrollGridWidget)) {
              this._scrollGridWidget = this;
            } else {
              let result = this.getParentWidget();
              while (result && !result.isInstanceOf(gbc.classes.ScrollGridWidget)) {
                result = result.getParentWidget();
              }
              this._scrollGridWidget = result;
            }
          }
          return this._scrollGridWidget;
        },

        /**
         * Get the stretchable scrollgrid Widget base class related to the widget
         * @returns {classes.StretchableScrollGridWidgetBase} StretchableScrollGridWidgetBase
         * @publicdoc
         */
        getStretchableScrollGridWidgetBase: function () {
          if (this._stretchableScrollGridWidgetBase === null) {
            if (this.isInstanceOf(gbc.classes.StretchableScrollGridWidgetBase)) {
              this._stretchableScrollGridWidgetBase = this;
            } else {
              let result = this.getParentWidget();
              while (result && !result.isInstanceOf(gbc.classes.StretchableScrollGridWidgetBase)) {
                result = result.getParentWidget();
              }
              this._stretchableScrollGridWidgetBase = result;
            }
          }
          return this._stretchableScrollGridWidgetBase;
        },

        /**
         * Get the Folder Widget related to the widget
         * @returns {classes.FolderWidget} FolderWidget
         * @publicdoc
         */
        getFolderWidget: function () {
          if (this._folderWidget === null) {
            if (this.isInstanceOf(gbc.classes.FolderWidget)) {
              this._folderWidget = this;
            } else {
              let result = this.getParentWidget();
              while (result && !result.isInstanceOf(gbc.classes.FolderWidget)) {
                result = result.getParentWidget();
              }
              this._folderWidget = result;
            }
          }
          return this._folderWidget;
        },

        /**
         * Get the parent Page Widget if there is any
         * @returns {classes.PageWidget | null} The parent PageWidget
         */
        getPageWidget: function () {
          if (this._pageWidget === null) {
            if (this.isInstanceOf(gbc.classes.PageWidget)) {
              this._pageWidget = this;
            } else {
              let result = this.getParentWidget();
              while (result && !result.isInstanceOf(gbc.classes.PageWidget)) {
                result = result.getParentWidget();
              }
              this._pageWidget = result;
            }
          }
          return this._pageWidget;
        },

        /**
         * Check if this widget is a child of a given one
         * @param {classes.WidgetBase} parent the reference parent widget
         * @return {boolean} true if is a child, false otherwise
         * @publicdoc
         */
        isChildOf: function (parent) {
          let result = this.getParentWidget();
          while (result && result !== parent) {
            result = result.getParentWidget();
          }
          return Boolean(result);
        },

        /**
         * Replace the current widget with a given one
         * @param {classes.WidgetBase} widget the new widget
         * @publicdoc
         */
        replaceWith: function (widget) {
          if (this._parentWidget) {
            this._parentWidget.replaceChildWidget(this, widget);
          }
        },

        /**
         * Detach the widget from the dom
         * @publicdoc
         */
        detach: function () {
          if (this._element && this._element.parentNode) {
            this._element.remove();
          } else {
            context.LogService.warn("Trying to detach a widget which is already outside of DOM " + this.__name);
          }
        },

        /**
         * Set widget current dialog type.
         * Can be Input, Input Array, Display, Display Array or Construct
         * @param {string} dialogType Dialog type
         * @publicdoc
         */
        setDialogType: function (dialogType) {
          this._dialogType = dialogType;
        },

        /**
         * return widget current dialog type
         * @returns {string} values can be : Input, InputArray, Display, DisplayArray or Construct
         * @publicdoc
         */
        getDialogType: function () {
          return this._dialogType;
        },

        /**
         * Defines the enabled status of the widget
         * @param {boolean} enabled true if the widget allows user interaction, false otherwise.
         * @publicdoc
         */
        setEnabled: function (enabled) {
          if (this._enabled !== Boolean(enabled)) {
            this._enabled = Boolean(enabled);
            this.toggleClasses(!this._enabled, "disabled");
            this._updateDOMTabIndexFromState();
            if (this._ariaDisabledExposed) {
              this.setAriaAttribute("disabled", this._enabled ? null : "true");
            }
          }
        },

        /**
         * Check if widget is enabled
         * @returns {boolean} true if the widget allows user interaction, false otherwise.
         * @publicdoc
         */
        isEnabled: function () {
          return this._enabled;
        },

        /**
         * `true` for action items that must stay discoverable by assistive
         * technologies while disabled: `setEnabled` then mirrors the state
         * as `aria-disabled` on `getAriaElement()`.
         * @type {boolean}
         * @protected
         */
        _ariaDisabledExposed: false,

        /**
         * `true` on a genuine activation key press (Space, or the two Enter
         * variants: main keyboard "return", numpad "enter"). Auto-repeat is
         * rejected: holding the key must not re-fire the action (a native
         * <button> does not repeat Space, and repeated toggles would flicker).
         * @param {string} keyString - GBC-normalized key
         * @param {boolean} [repeat] - true when the keydown is an OS auto-repeat
         * @returns {boolean}
         * @protected
         */
        _isActivationKey: function (keyString, repeat) {
          return !repeat && (keyString === "space" || keyString === "enter" || keyString === "return");
        },

        /**
         * Returns the DOM elements owned by this widget whose `tabindex` is
         * managed dynamically.
         * @returns {HTMLElement[]}
         */
        getDOMTabIndexElements: function () {
          return [];
        },

        /**
         * State predicate deciding whether the `getDOMTabIndexElements()`
         * entries belong to the browser Tab order. Default: enabled widget,
         * and visible for `hidesOutOfView()` widgets (kept rendered while
         * hidden, where `inert` isn't honored everywhere (TestCafe, pre-2023
         * browsers), so tabindex also tracks visibility; the common
         * `display:none` widgets already leave the Tab order when hidden).
         * Override to add widget-specific state (dialog type, sortability...).
         * @returns {boolean}
         * @protected
         */
        _isDOMTabbable: function () {
          return this.isEnabled() && (!this.hidesOutOfView() || this.isVisible());
        },

        /**
         * Synchronize the DOM `tabindex` on each `getDOMTabIndexElements()`
         * entry with `_isDOMTabbable()`: `"0"` when tabbable, `"-1"` otherwise.
         * `tabindex="-1"` keeps the element focusable programmatically
         * (VM-driven focus, `.focus()` from JS) while removing it from the
         * browser's Tab order.
         *
         * `setEnabled` calls this on every enabled-state change and `setHidden`
         * on visibility changes of `hidesOutOfView()` widgets; overrides adding
         * other state to the predicate must call it from the matching setters.
         * The template's default focusability must match the default widget
         * state (`_enabled: true`), otherwise sync at creation is mandatory:
         * a no-op `setEnabled` call does not re-sync.
         * @protected
         */
        _updateDOMTabIndexFromState: function () {
          const value = this._isDOMTabbable() ? "0" : "-1";
          for (const el of this.getDOMTabIndexElements()) {
            if (el && el.getAttribute("tabindex") !== value) {
              el.setAttribute("tabindex", value);
            }
          }
        },

        /**
         * `true` when the widget stays rendered (out of view) while hidden
         * instead of being dropped by the default `.hidden` `display: none`.
         * Such widgets (e.g. `MessageWidget`) need `inert` — applied by
         * `setHidden` — to leave the Tab order and a11y tree; the rest rely on
         * `display: none`. Override when `.hidden` is not `display: none`.
         * @returns {boolean}
         * @protected
         */
        hidesOutOfView: function () {
          return false;
        },

        /**
         * Show or hide the widget. The default `.hidden` (`display: none`)
         * already drops it from layout, Tab order and the a11y tree; widgets
         * kept rendered out of view (`hidesOutOfView()`) get `inert` instead.
         * @param {boolean} hidden true to hide the widget
         * @publicdoc
         */
        setHidden: function (hidden) {
          if (this._hidden === Boolean(hidden)) {
            return;
          }
          this._hidden = Boolean(hidden);
          this.toggleClasses(this._hidden, "hidden");
          // Only widgets kept rendered out of view need `inert` + a tabindex sync;
          // `display:none` already drops the rest from the Tab order and a11y tree.
          // (`inert` isn't honored everywhere — TestCafe, pre-2023 browsers — so the
          // managed elements also get `tabindex="-1"` as an inert-independent fallback.)
          if (this.hidesOutOfView()) {
            this._element.toggleAttribute("inert", this._hidden);
            this._updateDOMTabIndexFromState();
          }
          if (this._layoutEngine) {
            this._layoutEngine.changeHidden(hidden);
          }
          this.emit(context.constants.widgetEvents.visibilityChange);
        },

        /**
         * Check if the widget is hidden
         * @returns {boolean} true if the widget is hidden, false otherwise
         * @publicdoc
         * @deprecated
         */
        isHidden: function () {
          return !this.isVisible();
        },

        /**
         * Check if the widget is part of layout computing
         * @param {boolean} [deep] true to test against parent widgets as well
         * @returns {boolean} true if the widget is part of layout computing
         */
        isLayoutMeasureable: function (deep) {
          if (!deep) {
            return this.isVisible();
          }
          if (!this.isVisible()) {
            return false;
          }
          if (this.isLayoutTerminator() && this.isVisible()) {
            return true;
          }
          let parent = this.getParentWidget();
          while (parent) {
            if (!parent.isLayoutMeasureable()) {
              return false;
            }
            if (parent.isLayoutTerminator() && parent.isLayoutMeasureable()) {
              return true;
            }
            parent = parent.getParentWidget();
          }
          return true;
        },

        /**
         * Check if the widget is visible
         * @return {boolean} true if visible, false otherwise
         * @publicdoc
         */
        isVisible: function () {
          return !this._hidden;
        },

        /**
         * Check if widget and all of its parent are visible
         * @return {boolean} true if visible, false otherwise
         */
        isVisibleRecursively: function () {
          if (!this.isVisible()) {
            return false;
          }
          let parent = this.getParentWidget();
          while (parent) {
            if (!parent.isVisible()) {
              return false;
            }
            parent = parent.getParentWidget();
          }
          return true;
        },

        isLayoutTerminator: function () {
          return false;
        },

        /**
         * Remove or add borders to the widget
         * @param {boolean} noBorder - true if the widget has no border class, false otherwise
         * @publicdoc
         */
        setNoBorder: function (noBorder) {
          if (this._noBorder !== Boolean(noBorder)) {
            this._noBorder = Boolean(noBorder);
            this.toggleClasses(this._noBorder, "gbc_NoBorder");
          }
        },

        /**
         * Check if the widget is displayed without border
         * @returns {boolean} true if the widget has no border class, false otherwise
         * @publicdoc
         */
        isNoBorder: function () {
          return this._noBorder;
        },

        /**
         * The widget's own visible label, when it renders one. It is the
         * accessible name (WCAG 2.5.3); content widgets set it from setText.
         * @type {?string}
         */
        _labelText: null,

        /**
         * Last tooltip/comment string set through setTitle (or null).
         * @type {?string}
         */
        _comment: null,

        /**
         * Set the title of the widget
         * @param {string} title - the tooltip text
         * @publicdoc
         */
        setTitle: function (title) {
          const normalized = title || null;
          if (this._comment === normalized) {
            return;
          }
          this._comment = normalized;
          cls.DOMElementHelper.updateAttribute(this.getElement(), "title", normalized);
          this._refreshAccessibleName();
        },

        /**
         * Get the title of the widget
         * @returns {string} the tooltip text
         * @publicdoc
         */
        getTitle: function () {
          return this._element.getAttribute("title");
        },

        /**
         * Writes the accessible name on getAriaElement(): the widget's own
         * label when it has one, otherwise the comment (icon-only controls
         * with no label keep the comment as their name). A tooltip/comment
         * is a description, not a name, so it must not shadow a visible label
         * (WCAG 2.5.3). Separate from setTitle/setText so content widgets can
         * re-run it whichever of the two arrives last.
         * @protected
         */
        _refreshAccessibleName: function () {
          this.setAriaAttribute("label", this._labelText || this._comment || null);
        },

        /**
         * Called when widget obtains the focus
         * @param {boolean} [fromMouse] - true if focus comes from mouse event
         * @param {boolean} [preventScroll] - true if you want to prevent browser automatic scroll
         * @publicdoc
         */
        setFocus: function (fromMouse = false, preventScroll = false) {
          const userInterfaceWidget = this.getUserInterfaceWidget();
          if (userInterfaceWidget) {
            userInterfaceWidget.setFocusedWidget(this);
            // emit current view change (used for hbox splitview)
            userInterfaceWidget.emit(context.constants.widgetEvents.splitViewChange);
          }

          // rare case when we are going to focus a hidden widget. To avoid fallback focus to body, we focus userinterface widget instead.
          if (!this.isVisible()) {
            const uiWidget = this.getUserInterfaceWidget();
            if (uiWidget) {
              uiWidget.domFocus(preventScroll);
            }
          }
        },

        /**
         * Trigger the highlight class on the base component of the widget
         * @param {boolean} isHighlighted True to set the highlight class, false to remove it
         */
        setHighlight: function (isHighlighted) {
          this.toggleClass(gbc.constants.widgetCssClasses.isHighlighted, isHighlighted);
        },

        /**
         * Called before setting VM focus to notify previous VM focused widget
         * @param {classes.WidgetBase} vmNewFocusedWidget - new widget which get the focus
         * @publicdoc
         */
        loseVMFocus: function (vmNewFocusedWidget = null) {
          // if widget which lost focus is in a value container
          // and widget which get the focus is not in the same container
          // call lostVMFocus function on its container

          // table
          let oldTableWidgetBase = this.getTableWidgetBase();
          let newTableWidgetBase = vmNewFocusedWidget.getTableWidgetBase();
          if (oldTableWidgetBase && oldTableWidgetBase !== this && oldTableWidgetBase !== newTableWidgetBase) {
            oldTableWidgetBase.loseVMFocus(vmNewFocusedWidget);
            return;
          }

          // stretchable scrollgrid
          let oldStretchableScrollGridBase = this.getStretchableScrollGridWidgetBase();
          let newStretchableScrollGridBase = vmNewFocusedWidget.getStretchableScrollGridWidgetBase();
          if (
            oldStretchableScrollGridBase &&
            oldStretchableScrollGridBase !== this &&
            oldStretchableScrollGridBase !== newStretchableScrollGridBase
          ) {
            oldStretchableScrollGridBase.loseVMFocus(vmNewFocusedWidget);
          }
        },

        /**
         * Called before setFocus to notify previous focused widget
         * @publicdoc
         */
        loseFocus: function () {
          this.emit(gbc.constants.widgetEvents.loseFocus);
        },

        /**
         * Check if widget node has VM focus
         * @returns {boolean} true if widget node has VM focus
         * @publicdoc
         */
        hasFocus: function () {
          const ui = this.getUserInterfaceWidget();
          return !ui || this === ui.getFocusedWidget();
        },

        /**
         * Checks if the widget element has the given class
         * @param {string} className - class to check
         * @publicdoc
         */
        hasClass: function (className) {
          return this._element.classList.contains(className);
        },

        /**
         * Add the given CSS classes to the Widget Element
         * @param {string[]} classes - classes to add
         * @publicdoc
         */
        addClasses: function (...classes) {
          this.getElement().classList.add(...classes);
        },

        /**
         * Add the given class to element
         * @param {string} className - class to add
         * @publicdoc
         * @deprecated : Use addClasses instead.
         */
        addClass: function (className) {
          // TODO : Remove this method to use addClasses instead.
          // Thus avoiding multiple calls to add multiple classes
          this._element.classList.add(className);
        },

        /**
         * Remove the given class from element
         * @param {string} className - class to delete
         * @publicdoc
         */
        removeClass: function (className) {
          this._element.classList.remove(className);
        },

        /**
         * Toggle the given class to element
         * @param {string} className - class to toggle
         * @param {string|boolean} className2 - class added if switcher is false
         * @param {boolean=} switcher forced new state
         * @publicdoc
         */
        toggleClass: function (className, className2, switcher) {
          cls.DOMElementHelper.toggleClass(this._element, className, className2, switcher);
        },

        /**
         * Toggle the given class to element
         * @param {boolean} condition The condition on which to apply whenTrue or whenFalse
         * @param {string} whenTrue CSS Class to add when condition is true, and to remove when condititon is false
         * @param {string?} whenFalse Optional CSS Class to add when condition is false, and to remove when condition is true
         */
        toggleClasses: function (condition, whenTrue, whenFalse) {
          cls.DOMElementHelper.toggleClasses(this._element, condition, whenTrue, whenFalse);
        },

        /**
         * Add necessary CSS classes depending on the parent
         * TODO : This method should be splitted and moved into the `addChildWidget & adoptChildWidget` of respective widgets
         */
        updateCssClassesFromParenting: function () {
          this.toggleClass("gbc_WidgetBase_standalone", !(this._inTable || this._inScrollGrid));
          this.toggleClass(gbc.constants.widgetCssClasses.isStandalone, !(this._inTable || this._inScrollGrid || this._inMatrix));
          this.toggleClass("gbc_WidgetBase_in_array", this._inTable || this._inScrollGrid);
          this.toggleClass(gbc.constants.widgetCssClasses.isInTable, this._inTable);
          this.toggleClass(gbc.constants.widgetCssClasses.isInScrollGrid, this._inScrollGrid);
          // For GBC-4255 :  this line can be removed once the highlight system is fixed.
          // PLEASE DO NOT USE THIS CSS Class for anything else
          // Don't add Technical Debt
          this.toggleClass("gbc_WidgetBase_in_grid_matrix", this._inMatrix && !this._inScrollGrid);
          this.toggleClass(gbc.constants.widgetCssClasses.isInTable.isInMatrix, this._inMatrix && !this._inScrollGrid);
        },

        /**
         * Add QA information to the widget
         * @param {string} name - AUI tree name
         * @param {string} value - AUI tree value
         */
        setQAInfo: function (name, value) {
          cls.DOMElementHelper.updateQAAttribute(this.getElement(), name, value);
        },

        /**
         * Return the AUI name of the widget.
         * Corresponding to the name attribute of the AUI node
         * @returns {string?}
         */
        getAuiName: function () {
          return this._auiName;
        },

        /**
         * Defines the AUI tree name of the widget
         * @param {string} name the name
         */
        setAuiName: function (name) {
          if (this._element && name !== this._auiName) {
            this._auiName = name;
            cls.DOMElementHelper.updateAttribute(this.getElement(), "data-aui-name", name);
          }
        },

        /**
         * Check if the widget is in a table
         * @param {classes.TableWidgetBase} table - specific table, if null just return if widget is in any table
         * @returns {boolean} true if the widget is in a table, false otherwise.
         * @publicdoc
         */
        isInTable: function (table = null) {
          return this._inTable && (table === null || this.getTableWidgetBase() === table);
        },

        /**
         * Check if the widget is in a scrollGrid
         * @param {classes.WidgetBase} scrollGrid - specific scrollGrid, if null just return if widget is in any scrollGrid
         * @returns {boolean} true if the widget is in a table, false otherwise.
         * @publicdoc
         */
        isInScrollGrid: function (scrollGrid = null) {
          const inSpecificScrollGrid = this.getStretchableScrollGridWidgetBase() === scrollGrid || this.getScrollGridWidget() === scrollGrid;
          return this._inScrollGrid && (scrollGrid === null || inSpecificScrollGrid);
        },

        /**
         * Check if the widget is in a matrix
         * @returns {boolean} true if the widget is in a matrix, false otherwise.
         * @publicdoc
         */
        isInMatrix: function () {
          return this._inMatrix;
        },

        /**
         * Check if the widget is in an array (table, matrix or scrollgrid)
         * @returns {boolean} true if the widget is in an array, false otherwise.
         * @publicdoc
         */
        isInArray: function () {
          return this.isInTable() || this.isInMatrix() || this.isInScrollGrid();
        },

        /**
         * Does the widget ignore layouting
         * @returns {boolean} true if the widget ignore all layout.
         * @publicdoc
         */
        ignoreLayout: function () {
          return this._ignoreLayout;
        },

        /**
         * Set Arabic mode
         * @param {boolean} rtl - true if widget is right to left
         * @publicdoc
         */
        setReverse: function (rtl) {
          if (this._isReversed !== rtl) {
            this._isReversed = rtl;
            this.toggleClasses(rtl, "reverse");
          }
        },

        /**
         * Check if arabic mode is enabled
         * @return {boolean} true if enabled
         * @publicdoc
         */
        isReversed: function () {
          return this._isReversed;
        },

        /**
         * Get start (for reversed mode)
         * @return {string} start keyword for rtl
         * @publicdoc
         */
        getStart: function () {
          return this.isReversed() ? "right" : "left";
        },

        /**
         * Get end (for reversed mode)
         * @return {string} end keyword for rtl
         * @publicdoc
         */
        getEnd: function () {
          return this.isReversed() ? "left" : "right";
        },

        /**
         * Method called when the widget is attached/detached from the DOM
         * Override this in inherited widget if necessary
         */
        _setDOMAttachedOrDetached: function () {},

        /**
         * Returns if element is in the DOM
         * @return {boolean} true if element in the DOM
         */
        isElementInDOM: function () {
          return Boolean(this._element) && cls.DOMElementHelper.isInDOM(this._element);
        },

        /**
         * Could the widget get interrupt?
         * @param {boolean} interruptable - true if interruptable, false otherwise
         */
        setInterruptable: function (interruptable) {
          this._interruptable = interruptable;
          if (this._element) {
            cls.DOMElementHelper.updateAttribute(this.getElement(), "interruptable", interruptable ? "interruptable" : null);
          }
        },
        /**
         * returns true if widget acts as an interruptable
         * @return {boolean} true if widget acts as an interruptable
         */
        isInterruptable: function () {
          return this._interruptable;
        },

        /**
         * Updates widget interruptable active
         * @param isActive is interruptable active?
         */
        setInterruptableActive: function (isActive) {
          if (this._element) {
            cls.DOMElementHelper.updateAttribute(this.getElement(), "interruptable-active", isActive ? "interruptable-active" : null);
          }
        },

        /**
         * Make the widget flash (basically when some action are forbidden)
         * @param {Number?} duration - flash duration in ms (default 50ms)
         */
        flash: function (duration) {
          if (this.isEnabled()) {
            this.addClasses("disabled");
            this._registerTimeout(
              function () {
                this.removeClass("disabled");
              }.bind(this),
              duration || 50
            );
          }
        },

        /**
         * Returns if widget has cursors
         * @return {boolean} true if widget has cursors
         */
        hasCursors: function () {
          // if widget has setCursors & getCursors functions defined -> it supports cursors
          return this.setCursors && this.getCursors;
        },

        /**
         * Returns if we handle clipboard events
         * @returns {boolean} true if clipboard events are managed
         */
        handleClipboardEvent: function () {
          return true;
        },

        /**
         * Manage clipboard on paste data to the widget
         * @param {string} text - pasted text
         */
        manageClipboardPaste: function (text) {
          // Do whatever with text data
        },

        /**
         * Manage data to be copied to the clipboard
         * @param {string} copiedText - is the text copied by clipboard
         * @returns {string} the modified text, default is unchanged
         */
        manageClipboardCopy: function (copiedText) {
          // Do whatever with text data you want to be added to the clipboard when copy
          return copiedText;
        },

        /**
         * Manage cut data to be copied to the clipboards
         */
        manageClipboardCut: function () {},

        /**
         * Manage local action
         * @param {string} localActionName - local action name

         */
        manageLocalAction: function (localActionName) {},

        /**
         * Manage key
         * @param {string} keyString - key string representation
         * @param {Object} domKeyEvent - key event from DOM
         * @param {boolean} repeat - true if key is being pressed
         * @returns {boolean} returns if the domKeyEvent has been processed by the widget
         */
        manageKeyDown: function (keyString, domKeyEvent, repeat) {
          if (this.isInTable()) {
            return this.getTableWidgetBase().manageKeyDown(keyString, domKeyEvent, repeat);
          }
          return false;
        },

        /**
         * Manage key before any action
         * @param {string} keyString - key string representation
         * @param {Object} domKeyEvent - key event from DOM
         * @param {boolean} repeat - true if key is being pressed
         * @returns {boolean} returns if the domKeyEvent has been processed by the widget
         */
        managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
          if (this.isInTable()) {
            return this.getTableWidgetBase().managePriorityKeyDown(keyString, domKeyEvent, repeat);
          } else if (this.isInScrollGrid()) {
            let scrollGridWidget = this.getScrollGridWidget() || this.getStretchableScrollGridWidgetBase();
            return scrollGridWidget.managePriorityKeyDown(keyString, domKeyEvent, repeat);
          }
          return false;
        },

        /**
         * Manage key once released (on key up).
         * @param {string} keyString - keys string representation (can be a combinaison eg: shift+a)
         * @param {Object} domKeyEvent - key event from DOM
         */
        manageKeyUp: function (keyString, domKeyEvent) {
          if (this.isInTable()) {
            this.getTableWidgetBase().manageKeyUp(keyString, domKeyEvent);
          }
        },

        /**
         * Activate this widget as an active system widget.
         */
        _activateSystemWidget: function () {
          gbc.LogService.ui.log("SystemWidget open", this.__name, this);
          // Save the pre-modal focused element (before _startSystemFocusTracking moves focus into this widget).
          // Idempotent: if activate is called again without deactivate in between, keep the original pre-modal element
          // (otherwise the second call would capture a modal-internal element and break focus restoration).
          if (!this._preSystemFocusedElement) {
            this._preSystemFocusedElement = document.activeElement;
          }
          context.KeyboardService.registerSystemWidget(this);
          this._startSystemFocusTracking();
        },

        /**
         * Deactivate this widget as an active system widget.
         */
        _deactivateSystemWidget: function () {
          this._stopSystemFocusTracking();
          context.KeyboardService.unregisterSystemWidget(this);
          // Restore focus to the element that had it before this system widget opened
          if (
            this._preSystemFocusedElement &&
            this._preSystemFocusedElement !== document.body &&
            document.body.contains(this._preSystemFocusedElement) &&
            typeof this._preSystemFocusedElement.focus === "function"
          ) {
            this._preSystemFocusedElement.focus();
          }
          this._preSystemFocusedElement = null;
          gbc.LogService.ui.log("SystemWidget close", this.__name, this);
        },

        /**
         * Start tracking DOM focus inside this system widget.
         * Adds gbc_Focus to the child widget that receives DOM focus, mimicking VM focus behaviour.
         * Called automatically by KeyboardService.registerSystemWidget() when the system widget becomes active.
         */
        _startSystemFocusTracking: function () {
          cls.DOMElementHelper.on(this._element, "focusin.SystemFocus", (event) => {
            const widget = context.WidgetService.getWidgetFromElement(event.target);
            widget?.addClass(gbc.constants.widgetCssClasses.isFocused);
          });
          cls.DOMElementHelper.on(this._element, "focusout.SystemFocus", (event) => {
            const widget = context.WidgetService.getWidgetFromElement(event.target);
            widget?.removeClass(gbc.constants.widgetCssClasses.isFocused);
          });

          // Set the focus on the system widget
          this.domFocus();
        },

        /**
         * Stop tracking DOM focus inside this system widget.
         * Removes gbc_Focus from the last focused child widget and cleans up listeners.
         * Called automatically by KeyboardService.unregisterSystemWidget() when the system widget is deactivated.
         */
        _stopSystemFocusTracking: function () {
          cls.DOMElementHelper.off(this._element, "focusin.SystemFocus");
          cls.DOMElementHelper.off(this._element, "focusout.SystemFocus");
        },

        /**
         * Manage mouse down
         * @param {*} domEvent - mouse down event from DOM
         * @returns {boolean} returns if event must be bubbled to parent DOM widget
         */
        manageMouseDown: function (domEvent) {
          return true;
        },

        /**
         * Manage mouse up
         * @param {*} domEvent - mouse up event from DOM
         * @returns {boolean} returns if event must be bubbled to parent DOM widget
         */
        manageMouseUp: function (domEvent) {
          return true;
        },

        /**
         * Manage mouse click
         * @param {*} domEvent - mouse click event from DOM
         * @returns {boolean} returns if event must be bubbled to parent DOM widget
         */
        manageMouseClick: function (domEvent) {
          if (this.isInTable()) {
            this.emit(context.constants.widgetEvents.tableClick, domEvent);
          }
          return true;
        },

        /**
         * Bind a handler executed when click is executed
         * @param hook
         * @returns {Function} return handler reference
         */
        onClick: function (hook) {
          return this.when(context.constants.widgetEvents.click, hook);
        },

        /**
         * Manage mouse double click
         * @param {*} domEvent - mouse dblclick event from DOM
         * @returns {boolean} returns if event must be bubbled to parent DOM widget
         */
        manageMouseDblClick: function (domEvent) {
          return true;
        },

        /**
         * Manage mouse right click
         * @param {*} domEvent - mouse click event from DOM
         * @returns {boolean} returns if event must be bubbled to parent DOM widget
         */
        manageMouseRightClick: function (domEvent) {
          if (domEvent.shiftKey) {
            return false; // don't show context menu if shift key is pressed
          }

          if (context.DebugService.isActive() && domEvent.ctrlKey) {
            cls.EventHelper.preventCancelableDefault(domEvent);
            return false; // right click + CTRL is used to show debugTree
          }

          this._onRequestFocus(domEvent); // request focus
          if (this.isInTable()) {
            this.emit(context.constants.widgetEvents.tableClick, domEvent);
          }

          if (this.shouldShowApplicationContextMenu()) {
            const appWidget = this.getApplicationWidget();
            if (appWidget && context.ThemeService.getValue("gbc-disable-context-menu") === false) {
              if (!cls.DOMElementHelper.elementOrParent(domEvent.target, "gbc_ContextMenuWidget")) {
                // if right-click is not on a contextmenu
                appWidget.showContextMenu(domEvent, this);
              } else {
                // If right-click on context menu item: use a regular click instead
                cls.EventHelper.preventCancelableDefault(domEvent);
                this.manageMouseClick(domEvent);
              }
              return false; // if contextmenu is managed by this widget don't bubble
            }
          }
          return true;
        },

        /**
         * Manage input
         * @param {String} [dataString] - string with the inserted characters
         * @param {*} [event] - input event from DOM
         */
        manageInput: function (dataString = "", event = null) {},

        /**
         * Manage beforeinput
         * @param {String} [dataString] - string with the inserted characters
         * @param {*} [event] - beforeinput event from DOM
         * @return {boolean} returns true if ok, false if input is not allowed
         */
        manageBeforeInput: function (dataString = "", event = null) {
          return true;
        },

        /**
         * Define the aria role of this widget on its ARIA-bearing element (see {@link getAriaElement}).
         * Mostly already defined in template.
         * @param {string} roleName - aria role name to set
         */
        setAriaRole: function (roleName) {
          if (this._element) {
            cls.DOMElementHelper.updateAttribute(this.getAriaElement(), "role", roleName);
          }
        },

        /**
         * Get the aria role of this widget
         * @return {*} aria role value
         */
        getAriaRole: function () {
          return this.getAriaElement()?.getAttribute("role");
        },

        /**
         * Element on which this widget's ARIA attributes are written and read. Defaults to the
         * widget root since most widgets carry their role on the root. Override when the
         * role-bearing element differs — e.g. FieldWidgetBase uses the inner input (textbox /
         * combobox / spinbutton), CheckBoxWidget keeps the root because its inner input is
         * aria-hidden.
         * @returns {HTMLElement}
         */
        getAriaElement: function () {
          return this.getElement();
        },

        /**
         * Set an ARIA attribute on this widget's role-bearing element (see {@link getAriaElement}).
         * @param {string} attrName - aria attribute Name to set
         * @param {*} attrVal - aria attribute value to set, or null/false to remove
         */
        setAriaAttribute: function (attrName, attrVal) {
          if (attrName) {
            cls.DOMElementHelper.updateAttribute(this.getAriaElement(), "aria-" + attrName, attrVal);
          }
        },

        /**
         * Get the aria attribute of this widget,
         * @param {string} attrName - aria attribute Name to get
         * @return {*} aria attribute value
         */
        getAriaAttribute: function (attrName) {
          return this.getAriaElement()?.getAttribute(`aria-${attrName}`);
        },

        /**
         * Get the value to put in the clipboard when copying
         * @param {boolean} [ignoreSelection] true to send the widget value. False by default
         * @return {?string}
         */
        getValueForClipboard: function (ignoreSelection = false) {
          return null;
        },

        /**
         * Helper method to update attirbutes in DOM using buffering system
         * @param {string} attr the attribute name
         * @param {*} val the attribute new value
         * @param {string|Function} [elementSelector] a string identifier of this class member or a method returning the element to set the attributes value
         * @protected
         * @deprecated Use HTMLElement.updateAttribute instead
         * TODO : Remove this methode and replace all of it's usage
         */
        _setElementAttribute: function (attr, val, elementSelector) {
          let target = null;
          if (elementSelector) {
            if (typeof elementSelector === "string") {
              target = this[elementSelector];
            } else if (typeof elementSelector === "function") {
              target = elementSelector(this);
            }
          } else {
            target = this._element;
          }
          if (target) {
            if (val === null || val === "" || typeof val === "undefined") {
              target.removeAttribute(attr);
            } else {
              target.setAttribute(attr, val.toString());
            }
          }
        },

        /**
         * Helper method to update textContent in DOM using buffering system
         * @param {string} text the new text
         * @param {string|function} [elementSelector] a string identifier of this class member or a method returning the element to set the textContent
         * @protected
         */
        _setTextContent: function (text, elementSelector) {
          this.domAttributesMutator(
            function (text, elementSelector) {
              let target = null;
              if (elementSelector) {
                if (typeof elementSelector === "string") {
                  target = this[elementSelector];
                } else if (typeof elementSelector === "function") {
                  target = elementSelector(this);
                }
              } else {
                target = this._element;
              }
              if (target) {
                target.textContent = text;
              }
            }.bind(this, text, elementSelector)
          );
        },

        /**
         * Use this to update attributes of dom nodes using a buffering system
         * @param fn the function to bufferize - don't forget to bind to context
         */
        domAttributesMutator: function (fn) {
          const appWidget = this.getApplicationWidget();
          if (!appWidget || !appWidget.domAttributesMutationBuffer(fn, this)) {
            fn();
          }
        },

        /**
         * @param fn the function to bufferize - don't forget to bind to context
         */
        afterDomMutator: function (fn) {
          const appWidget = this.getApplicationWidget();
          if (!appWidget || !appWidget.afterDomMutationBuffer(fn, this)) {
            this._registerAnimationFrame(fn); // TODO why doing requestAnimationFrame in this case ?
          }
        },

        /**
         * True if the widget accept input when it is inside inactive windows
         * @return {boolean}
         */
        acceptEventWhenWindowInactive: function () {
          return this._acceptEventWhenWindowInactive;
        },

        /**
         * Set if the widget accept events inside inactive windows
         * @param {boolean} acceptEvent
         */
        setAcceptEventWhenWindowInactive: function (acceptEvent) {
          this._acceptEventWhenWindowInactive = acceptEvent;
        },

        /**
         * Add a behaviour name to the applied behaviours
         * @param {string} name behaviour name
         */
        addAppliedBehavior: function (name) {
          this._applyedBehaviors.add(name);
        },

        /**
         * true if the behaviour wad already applied
         * @param {string} name behaviour name
         * @return {boolean}
         */
        isAppliedBehavior: function (name) {
          return this._applyedBehaviors.has(name);
        },

        /**
         * Get the contextmenu authorized actions
         * @returns {object}
         */
        getContextMenuAuthorizedActions: function () {
          return {
            paste: false,
            copy: false,
            cut: false,
            selectAll: false
          };
        }
      };
    }
  );
});
